/**
 * Events, and the sessions that are the real grain of everything operational.
 *
 * The single most important shape here is that an event is a *series of sessions* (D14), not a
 * date range with one registration deadline. The pilot community's own words settled it:
 * registration closes at 12:30pm for the 1pm Grand Entry and reopens at 2pm for the 7pm one.
 * Payouts are daily, judge panels change between sessions, and registration windows are
 * per session — so the session is the unit that money, entry and judging all share.
 *
 * A community that runs one afternoon expresses it as one session with one window, so the
 * simple case costs nothing.
 */

import { index, integer, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import {
  archivable,
  audited,
  calendarDate,
  currency,
  flag,
  id,
  instant,
  json,
  ref,
  sortOrder,
} from "./columns.js";

/**
 * The lifecycle the system enforces. The feature list's ten public-facing statuses are derived,
 * not stored: "registration open/closed/scheduled" comes from the windows, "results under
 * review/published" from the result sets. Storing them here would let the label and the truth
 * drift apart mid-event (D36).
 */
export const EVENT_STATUSES = [
  "draft",
  "published",
  "in_progress",
  "complete",
  "archived",
  "cancelled",
] as const;

export const EVENT_VISIBILITIES = ["public", "private"] as const;

/**
 * What one act of checking in covers (D66).
 *
 * `session` is the gate: a dancer arrives once on the Saturday and is present for everything they
 * entered that day, which is how a single arena with a single entrance actually runs. `competition`
 * is the staging area: each competition takes its own check-in, which is what a community with
 * separate call-ups for the drum contest and the dance contest needs in order to know who is
 * actually standing there.
 *
 * Only the *act* differs. Both write the same per-entry rows against the same session, so the
 * payout calculator, the roster counts and reversal are untouched by the setting — which is why
 * this is a setting rather than a second way for money to be computed.
 */
export const CHECK_IN_UNITS = ["session", "competition"] as const;

export const events = sqliteTable(
  "events",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    slug: text("slug").notNull(),
    name: text("name").notNull(),
    /** What the poster says, when it differs from the working name. Null falls back to `name`. */
    publicTitle: text("public_title"),
    description: text("description"),
    /** One or two sentences for the community homepage card. */
    summary: text("summary"),

    /** Calendar dates, never instants — the 14th is the 14th wherever you are reading from. */
    startsOn: calendarDate("starts_on").notNull(),
    endsOn: calendarDate("ends_on").notNull(),

    /** Defaults from the community but is overridable, because an event can be held elsewhere. */
    timezone: text("timezone").notNull(),
    venue: text("venue"),
    venueAddress: text("venue_address"),
    city: text("city"),
    region: text("region"),
    postalCode: text("postal_code"),
    country: text("country"),
    mapUrl: text("map_url"),

    /** Poster contact for this event. Phone first, per the published posters (D21). */
    contactName: text("contact_name"),
    contactEmail: text("contact_email"),
    contactPhone: text("contact_phone"),

    status: text("status", { enum: EVENT_STATUSES }).notNull().default("draft"),
    /** Private events are reachable by staff and by direct link, never listed on the site. */
    visibility: text("visibility", { enum: EVENT_VISIBILITIES }).notNull().default("public"),
    currency: currency(),

    /**
     * The house target panel size for judge assignment (D56) — the number the coverage badge
     * counts toward and readiness measures against, defaulting to five as the client named. A
     * competition overrides it with its own `panelSize`. It is a target, never a cap: a panel is
     * emergent and provisioned on the day (D15), so more or fewer judges may still be assigned.
     */
    defaultPanelSize: integer("default_panel_size").notNull().default(5),

    /** What one check-in covers — the session, or each competition separately (D66). */
    checkInUnit: text("check_in_unit", { enum: CHECK_IN_UNITS }).notNull().default("session"),
    /**
     * Whether a participant may check themselves in from their own phone (D66).
     *
     * Off by default. A community that wants every arrival seen by a person at a table should not
     * have to notice a setting to keep that, and the gate is the one place where the wrong default
     * is discovered on the morning it matters.
     */
    selfCheckIn: flag("self_check_in").notNull().default(false),
    /**
     * Whether arriving sends the participant a confirmation email.
     *
     * Off by default, for the same reason `selfCheckIn` is and for one more: a gate scans hundreds
     * of people in an afternoon, and a community that has not asked for hundreds of emails should
     * not discover them being sent. A community that wants the receipt turns it on.
     */
    checkInEmail: flag("check_in_email").notNull().default(false),

    publishedAt: instant("published_at"),

    /**
     * The prose blocks of the public page — registration instructions, general rules,
     * cancellation, accessibility, parking, travel, accommodation, emergency. One JSON column
     * rather than eight text columns because they are always read together, never queried by,
     * and the set will grow as communities ask for more.
     */
    infoJson: json("info_json"),

    /**
     * Which credential types judges may use for this event (D15). A community that vets judges
     * weeks ahead keeps invitations and magic links; another hands out codes at the gate; a
     * third does both. This is the setting that keeps both paths first-class.
     */
    judgeAccessPolicyJson: json("judge_access_policy_json"),

    ...audited(),
    ...archivable(),
  },
  (table) => [
    uniqueIndex("events_community_slug_unique").on(table.communityId, table.slug),
    index("events_community_status_idx").on(table.communityId, table.status),
  ],
);

/**
 * A session. Saturday has two.
 *
 * `grandEntryLocal` and `grandEntryAt` are both stored on purpose: the schedule is written in
 * local wall-clock time and must survive an edit to the event's timezone. Keeping only the
 * instant would silently move a 1pm Grand Entry when someone corrected the zone.
 */
export const eventSessions = sqliteTable(
  "event_sessions",
  {
    id: id(),
    eventId: ref("event_id").notNull(),
    communityId: ref("community_id").notNull(),
    name: text("name").notNull(),
    date: calendarDate("date").notNull(),

    startsLocal: text("starts_local"),
    startsAt: instant("starts_at"),
    grandEntryLocal: text("grand_entry_local"),
    grandEntryAt: instant("grand_entry_at"),
    endsLocal: text("ends_local"),
    endsAt: instant("ends_at"),

    sortOrder: sortOrder(),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("event_sessions_event_idx").on(table.eventId, table.sortOrder)],
);

export const WINDOW_STATUSES = ["scheduled", "open", "closed"] as const;

/**
 * What a window governs.
 *
 * Registration and check-in ask the identical question — *is this open, for this session, for this
 * competition, right now* — and the answer is resolved by the identical rule: the most specific
 * window wins, a staff override beats the clock, no window at all means not gated (D44, D49).
 * Splitting them into two tables would have meant a second copy of that resolution living somewhere
 * else, and the two copies disagreeing is a thing discovered at a gate.
 *
 * So one table, one `purpose`, exactly as `participant_tokens.purpose` splits the manage credential
 * from the check-in one (D66): the shape is shared, the meaning is named, and every read states
 * which kind it wants.
 */
export const WINDOW_PURPOSES = ["registration", "check_in"] as const;

/**
 * When registration is open (D14, D20, D49).
 *
 * A window scopes to a *set* of sessions through `registrationWindowSessions` — no sessions means
 * event-wide, some sessions means those and only those. A null bound means open-ended. So a single
 * deadline for a whole event is one row with no session links, "registration held daily" is one row
 * per session (or one row linked to the days it runs), and rolling registration is a row with no
 * bounds at all.
 *
 * Windows are openable and closable **by hand during the event**, because two of the published
 * posters say registration timing is announced on the day. `openedBy` and `closedBy` record who
 * did it; the audit log records why.
 */
export const registrationWindows = sqliteTable(
  "registration_windows",
  {
    id: id(),
    eventId: ref("event_id").notNull(),
    communityId: ref("community_id").notNull(),

    /**
     * Registration or check-in. Defaults to `registration`, which is what every row written before
     * this column existed meant — a backfill by default rather than by migration script.
     */
    purpose: text("purpose", { enum: WINDOW_PURPOSES }).notNull().default("registration"),

    opensLocal: text("opens_local"),
    opensAt: instant("opens_at"),
    closesLocal: text("closes_local"),
    closesAt: instant("closes_at"),

    status: text("status", { enum: WINDOW_STATUSES }).notNull().default("scheduled"),
    openedBy: ref("opened_by"),
    openedAt: instant("opened_at"),
    closedBy: ref("closed_by"),
    closedAt: instant("closed_at"),
    notes: text("notes"),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("registration_windows_event_idx").on(table.eventId, table.purpose)],
);

/**
 * Which sessions a window governs (D49). Present only when a window is aimed at some sessions rather
 * than the whole event — an empty set is event-wide, exactly as an empty competition set governs
 * every competition. No `archivable()`: like the competition link, it lives and dies with the row.
 */
export const registrationWindowSessions = sqliteTable(
  "registration_window_sessions",
  {
    id: id(),
    windowId: ref("window_id").notNull(),
    sessionId: ref("session_id").notNull(),
    communityId: ref("community_id").notNull(),
    ...audited(),
  },
  (table) => [
    uniqueIndex("registration_window_sessions_unique").on(table.windowId, table.sessionId),
    index("registration_window_sessions_session_idx").on(table.sessionId),
  ],
);

/** Present only when a window governs some competitions rather than all of them. */
export const registrationWindowCompetitions = sqliteTable(
  "registration_window_competitions",
  {
    id: id(),
    windowId: ref("window_id").notNull(),
    competitionId: ref("competition_id").notNull(),
    communityId: ref("community_id").notNull(),
    ...audited(),
  },
  (table) => [
    uniqueIndex("registration_window_competitions_unique").on(table.windowId, table.competitionId),
  ],
);

// MC, Co-MC, Arena Director, Elder Advisor, Host Drum, Co-Host Drum and invited drums used to live
// here as `event_team`, with a free-text `role_label` and nothing else. They are now `event_roles`
// and `event_role_assignments` in ./roles.ts — configurable, countable, session-scoped, checkable in
// and payable (D62). The table was upgraded rather than duplicated, because two lists of who the MC
// is drift the day someone edits one of them.

export const ANNOUNCEMENT_CHANNELS = ["site", "push", "both"] as const;
export const ANNOUNCEMENT_PRIORITIES = ["normal", "important", "urgent"] as const;
export const ANNOUNCEMENT_VISIBILITIES = ["public", "internal"] as const;

/**
 * An update — "Registration closes Friday", "Grand Entry delayed to 1:30". Public ones render on
 * the event page until they expire; internal ones stay on the organiser's dashboard. Emailing
 * participants is a flag rather than a separate send screen, so the update and its email cannot
 * say different things.
 */
export const announcements = sqliteTable(
  "announcements",
  {
    id: id(),
    eventId: ref("event_id").notNull(),
    communityId: ref("community_id").notNull(),
    /** Present when the update concerns one competition — "Jigging moved to the gym". */
    competitionId: ref("competition_id"),
    title: text("title").notNull(),
    body: text("body").notNull(),
    publishAt: instant("publish_at"),
    publishedAt: instant("published_at"),
    expiresAt: instant("expires_at"),
    priority: text("priority", { enum: ANNOUNCEMENT_PRIORITIES }).notNull().default("normal"),
    visibility: text("visibility", { enum: ANNOUNCEMENT_VISIBILITIES }).notNull().default("public"),
    pinned: flag("pinned").notNull().default(false),
    /** When set, publishing also queues one email per registered participant with an address. */
    emailParticipants: flag("email_participants").notNull().default(false),
    channel: text("channel", { enum: ANNOUNCEMENT_CHANNELS }).notNull().default("site"),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("announcements_event_idx").on(table.eventId, table.publishAt)],
);

/**
 * Files in R2. `entityType`/`entityId` rather than a column per owner, because logos, posters,
 * rule documents, consent records and generated exports all want the same handling and none of
 * them is worth its own table.
 *
 * `entityType` names the *role*, not just the owning table — `event.poster` and
 * `announcement.image` are different things hanging off different rows, and a role in the string
 * is what lets one query fetch the posters without a second column saying which file is the
 * poster (D71).
 *
 * The three display columns exist for the public page rather than for the file. `altText` is what
 * a screen reader says and is the organiser's words, so it is nullable and asked for rather than
 * derived. `width`/`height` are recorded by the browser that did the upload, because the Worker
 * cannot decode an image to find them and the page needs the ratio *before* the bytes arrive —
 * without it every poster shoves the page down as it loads.
 */
export const attachments = sqliteTable(
  "attachments",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    entityType: text("entity_type").notNull(),
    entityId: text("entity_id").notNull(),
    r2Key: text("r2_key").notNull(),
    filename: text("filename").notNull(),
    mime: text("mime").notNull(),
    sizeBytes: integer("size_bytes").notNull(),
    altText: text("alt_text"),
    width: integer("width"),
    height: integer("height"),
    uploadedBy: ref("uploaded_by"),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("attachments_entity_idx").on(table.entityType, table.entityId)],
);
